import { useMemo, type ChangeEvent, type RefObject } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Upload } from 'lucide-react';
import { useCompanyAccounts } from '@/lib/query/hooks/useCompanies';
import {
  useMiseSellerGroupsCurrent,
  useMiseSellersCurrent,
} from '@/lib/query/hooks/useMiseSellers';
import { useMiseSellers, useMiseSellerGroups } from '@/lib/query/hooks/useMiseAccounts';
import { indentSellerGroupLabel, sortSellerGroupsByTree } from '@/lib/mise/sellerGroupTree';
import { buildSellerTreeOptions } from '@/lib/mise/sellerTree';
import { SellerTreeOptionList } from '@/components/mise/SellerTreeOptionList';
import { EXCLUDED_SELLER_ID } from '@/lib/types/mise/seller';
import { downloadBulkUserCsv, generateBulkUserTemplateCsv } from '@/lib/utils/csv-bulk-user';

export type ComparisonType = 'seller_group_mapping' | 'seller' | 'seller_group';

interface BulkCreateUserUploadStepProps {
  companyId: string;
  isPending: boolean;
  defaultAccountId: string;
  setDefaultAccountId: (v: string) => void;
  comparisonType: ComparisonType;
  setComparisonType: (v: ComparisonType) => void;
  comparisonSellerId: string;
  setComparisonSellerId: (v: string) => void;
  comparisonSellerGroupId: string;
  setComparisonSellerGroupId: (v: string) => void;
  suppressInvitationEmail: boolean;
  setSuppressInvitationEmail: (v: boolean) => void;
  fileName: string;
  parseErrors: string[];
  validateError: string | null;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export function BulkCreateUserUploadStep({
  companyId,
  isPending,
  defaultAccountId,
  setDefaultAccountId,
  comparisonType,
  setComparisonType,
  comparisonSellerId,
  setComparisonSellerId,
  comparisonSellerGroupId,
  setComparisonSellerGroupId,
  suppressInvitationEmail,
  setSuppressInvitationEmail,
  fileName,
  parseErrors,
  validateError,
  fileInputRef,
  onFileChange,
}: BulkCreateUserUploadStepProps) {
  const { data: accounts = [], isLoading: accountsLoading } = useCompanyAccounts(companyId);
  const { data: sellersCurrent = [], isLoading: sellersCurrentLoading } =
    useMiseSellersCurrent(defaultAccountId);
  const { data: sellersSnapshot = [], isLoading: sellersSnapshotLoading } =
    useMiseSellers(defaultAccountId);
  const sellers = useMemo(() => {
    const source = sellersCurrent.length > 0 ? sellersCurrent : sellersSnapshot;
    return source.filter((s) => s.id !== EXCLUDED_SELLER_ID);
  }, [sellersCurrent, sellersSnapshot]);
  // 店舗・店舗グループを階層表示するための階層データ（snapshot_hot 由来）
  const { data: sellerGroupNodes = [], isLoading: sellerGroupNodesLoading } =
    useMiseSellerGroups(defaultAccountId);
  const sellersLoading = defaultAccountId
    ? sellersCurrentLoading || sellersSnapshotLoading || sellerGroupNodesLoading
    : false;
  // 店舗を所属する店舗グループの階層の下に並べる（所属は snapshot_hot.sellers から引く）
  const sellerTreeOptions = useMemo(
    () => buildSellerTreeOptions(sellers, sellerGroupNodes, sellersSnapshot),
    [sellers, sellerGroupNodes, sellersSnapshot],
  );
  const { data: sellerGroups = [], isLoading: sellerGroupsCurrentLoading } =
    useMiseSellerGroupsCurrent(defaultAccountId);
  const sellerGroupsLoading = sellerGroupsCurrentLoading || sellerGroupNodesLoading;
  // 同名の店舗グループを見分けられるよう、階層順に並べて字下げする
  const sellerGroupOptions = useMemo(
    () => sortSellerGroupsByTree(sellerGroups, sellerGroupNodes),
    [sellerGroups, sellerGroupNodes],
  );

  const handleDownloadTemplate = () => {
    const csv = generateBulkUserTemplateCsv();
    downloadBulkUserCsv('mise_users_template.csv', csv);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <Typography>
        CSVファイル(user_email, seller_id, user_name)をアップロードしてユーザーを一括登録します
      </Typography>
      <Typography variant="caption" color="text.secondary">
        user_name は任意です。列が無い・空欄の場合はメールアドレスの@より前をユーザー名にします
      </Typography>
      <Typography variant="caption" color="text.secondary">
        1回のアップロードで登録できるユーザー数は最大500件です
      </Typography>

      <TextField
        select
        label="デフォルトアカウント"
        value={defaultAccountId}
        onChange={(e) => {
          setDefaultAccountId(e.target.value);
          setComparisonSellerId('');
          setComparisonSellerGroupId('');
        }}
        required
        fullWidth
        disabled={isPending || accountsLoading}
      >
        {accountsLoading ? (
          <MenuItem disabled>読み込み中...</MenuItem>
        ) : accounts.length === 0 ? (
          <MenuItem disabled>アカウントがありません</MenuItem>
        ) : (
          accounts.map((account) => (
            <MenuItem key={account.id} value={account.id}>
              {account.name || account.id} {account.code && `(${account.code})`}
            </MenuItem>
          ))
        )}
      </TextField>

      {defaultAccountId && (
        <>
          <FormControl component="fieldset">
            <FormLabel component="legend">比較店舗の指定方法 *</FormLabel>
            <RadioGroup
              row
              value={comparisonType}
              onChange={(e) => {
                setComparisonType(e.target.value as ComparisonType);
                setComparisonSellerId('');
                setComparisonSellerGroupId('');
              }}
            >
              <FormControlLabel
                value="seller_group_mapping"
                control={<Radio disabled={isPending} />}
                label="店舗グループマッピングに従う"
              />
              <FormControlLabel
                value="seller"
                control={<Radio disabled={isPending} />}
                label="比較店舗で指定"
              />
              <FormControlLabel
                value="seller_group"
                control={<Radio disabled={isPending} />}
                label="比較店舗グループで指定"
              />
            </RadioGroup>
          </FormControl>

          {comparisonType === 'seller_group_mapping' && (
            <Typography variant="body2" color="text.secondary">
              各ユーザーの店舗が属する店舗グループ（集計後の店舗-店舗グループマッピング）を、比較店舗グループに設定します。生成AIの比較分析と同じ比較先です。
            </Typography>
          )}

          {comparisonType === 'seller' && (
            <TextField
              select
              label="比較店舗"
              value={comparisonSellerId}
              onChange={(e) => setComparisonSellerId(e.target.value)}
              required
              fullWidth
              disabled={isPending || sellersLoading}
              slotProps={{ inputLabel: { shrink: true }, select: { native: true } }}
            >
              <option value="">
                {sellersLoading
                  ? '読み込み中...'
                  : sellers.length === 0
                    ? '店舗がありません'
                    : '選択してください'}
              </option>
              <SellerTreeOptionList options={sellerTreeOptions} />
            </TextField>
          )}

          {comparisonType === 'seller_group' && (
            <TextField
              select
              label="比較店舗グループ"
              value={comparisonSellerGroupId}
              onChange={(e) => setComparisonSellerGroupId(e.target.value)}
              required
              fullWidth
              disabled={isPending || sellerGroupsLoading}
              slotProps={{ inputLabel: { shrink: true }, select: { native: true } }}
            >
              <option value="">
                {sellerGroupsLoading
                  ? '読み込み中...'
                  : sellerGroups.length === 0
                    ? '店舗グループがありません'
                    : '選択してください'}
              </option>
              {sellerGroupOptions.map(({ group, depth }) => (
                <option key={group.id} value={group.id}>
                  {indentSellerGroupLabel(depth, group.name || group.id)}
                </option>
              ))}
            </TextField>
          )}
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={suppressInvitationEmail}
            onChange={(e) => setSuppressInvitationEmail(e.target.checked)}
            disabled={isPending}
          />
        }
        label="招待メールを送信しない"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="text" onClick={handleDownloadTemplate} disabled={isPending}>
          テンプレートCSVをダウンロード
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<Upload size={16} />}
          disabled={isPending}
        >
          CSVファイルを選択
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={onFileChange}
          />
        </Button>
        <Typography variant="body2" color="text.secondary">
          {fileName || 'ファイル未選択'}
        </Typography>
      </Box>

      {parseErrors.length > 0 && (
        <Alert severity="error">
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {parseErrors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </Box>
        </Alert>
      )}

      {validateError && <Alert severity="error">{validateError}</Alert>}
    </Box>
  );
}
